import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, FilterAltOff as FilterAltOffIcon } from '@mui/icons-material';
import {
  api,
  ProjectActionCoreTeamMember,
  ProjectActionProjectSummary,
  ProjectActionProjectTasks,
} from '../../../services/api';
import {
  ColumnHeadCell,
  ColumnMenuPopover,
  EmptyNote,
  formatDateTime,
  InlineCommentCell,
  leadingNumber,
  matchesText,
  paHeadCellSx,
  paTableContainerSx,
  SortDirection,
} from './shared';

type SortKey = 'agent' | 'mobile' | 'why' | 'assigned' | 'progress';

const UNASSIGNED_KEY = 'UNASSIGNED';

interface BoardFilters {
  agent: string;
  mobile: string;
  why: string;
  /** CEA Reg Nos, plus UNASSIGNED_KEY. Empty means no filter. */
  assigned: string[];
  /** 'PENDING' | 'CALLED'. Empty means no filter. */
  progress: string[];
}

const NO_FILTERS: BoardFilters = { agent: '', mobile: '', why: '', assigned: [], progress: [] };

const FILTERABLE_COLUMNS: SortKey[] = ['agent', 'mobile', 'why', 'assigned', 'progress'];

const COLUMN_LABELS: Record<SortKey, string> = {
  agent: 'Agent',
  mobile: 'Mobile',
  why: 'Why',
  assigned: 'Assigned to',
  progress: 'Progress',
};

function columnHasFilter(filters: BoardFilters, key: SortKey): boolean {
  switch (key) {
    case 'agent':
      return Boolean(filters.agent.trim());
    case 'mobile':
      return Boolean(filters.mobile.trim());
    case 'why':
      return Boolean(filters.why.trim());
    case 'assigned':
      return filters.assigned.length > 0;
    case 'progress':
      return filters.progress.length > 0;
    default:
      return false;
  }
}

function filtersAreEmpty(filters: BoardFilters): boolean {
  return FILTERABLE_COLUMNS.every((key) => !columnHasFilter(filters, key));
}


interface FilterOption {
  key: string;
  label: string;
}

/**
 * What a project's lead sees: every agent on the project's lists, and the means
 * to share them out across the core team with a brief attached.
 *
 * Two levels, so this is a drill-down — the project picker first, then that
 * project's board.
 */
const ProjectBoardTab: React.FC = () => {
  const [summaries, setSummaries] = useState<ProjectActionProjectSummary[]>([]);
  const [board, setBoard] = useState<ProjectActionProjectTasks | null>(null);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{ column: SortKey | null; direction: SortDirection }>(
    { column: null, direction: 'default' }
  );
  const [filters, setFilters] = useState<BoardFilters>(NO_FILTERS);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; column: SortKey } | null>(
    null
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [assignee, setAssignee] = useState<ProjectActionCoreTeamMember | null>(null);
  const [brief, setBrief] = useState('');
  const [assigning, setAssigning] = useState(false);

  const loadSummaries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getProjectActionProjects();
      setSummaries(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBoard = useCallback(async (projectId: string) => {
    setLoading(true);
    setError(null);
    try {
      setBoard(await api.getProjectActionProjectTasks(projectId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the project list');
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openProjectId) loadBoard(openProjectId);
    else loadSummaries();
  }, [openProjectId, loadBoard, loadSummaries]);

  // Leaving a project drops the selection with it — the ids mean nothing on
  // another project's board. Switching list within a project does the same,
  // since the rows on screen change underneath.
  useEffect(() => {
    setSelected(new Set());
    setAssignee(null);
    setBrief('');
    setNotice(null);
    setFilters(NO_FILTERS);
    setSortConfig({ column: null, direction: 'default' });
  }, [openProjectId, selectedListId]);

  const allTasks = board?.tasks || [];

  /** The lists represented on this project's board, with their own counts. */
  const listOptions = useMemo(() => {
    const byId = new Map<number, { id: number; title: string; total: number; unassigned: number }>();
    for (const task of allTasks) {
      const entry = byId.get(task.listId) || {
        id: task.listId,
        title: task.listTitle || `List ${task.listId}`,
        total: 0,
        unassigned: 0,
      };
      entry.total += 1;
      if (!task.assignedToCEANumber) entry.unassigned += 1;
      byId.set(task.listId, entry);
    }
    return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [allTasks]);

  // One list needs no choosing. Several do — they are different cohorts pulled
  // for different reasons, and a table mixing them would not mean anything.
  useEffect(() => {
    setSelectedListId((current) => {
      if (current && listOptions.some((l) => l.id === current)) return current;
      return listOptions.length === 1 ? listOptions[0].id : null;
    });
  }, [listOptions]);

  const activeList = useMemo(
    () => listOptions.find((l) => l.id === selectedListId) || null,
    [listOptions, selectedListId]
  );
  const tasks = useMemo(
    () => (selectedListId ? allTasks.filter((t) => t.listId === selectedListId) : []),
    [allTasks, selectedListId]
  );

  /** Who the list is currently shared with, for the "Assigned to" filter. */
  const assigneeOptions = useMemo(() => {
    const byCea = new Map<string, string>();
    for (const task of tasks) {
      if (!task.assignedToCEANumber) continue;
      byCea.set(task.assignedToCEANumber, task.assignedToName || task.assignedToCEANumber);
    }
    return Array.from(byCea.entries())
      .map(([ceaNumber, name]) => ({ ceaNumber, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    const filtered = tasks.filter((task) => {
      if (!matchesText(`${task.agentName || ''} ${task.agentCEANumber}`, filters.agent)) return false;
      if (!matchesText(task.agentMobile, filters.mobile)) return false;
      if (!matchesText(task.remarks, filters.why)) return false;
      if (filters.assigned.length > 0) {
        const key = task.assignedToCEANumber || UNASSIGNED_KEY;
        if (!filters.assigned.includes(key)) return false;
      }
      if (filters.progress.length > 0) {
        if (!filters.progress.includes(task.calledAt ? 'CALLED' : 'PENDING')) return false;
      }
      return true;
    });

    // 'default' keeps the server's order (agent name ascending).
    if (!sortConfig.column || sortConfig.direction === 'default') return filtered;
    const orderBy = sortConfig.column;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (orderBy) {
        case 'why': {
          // Numeric where the remark starts with a count; rows without one sort
          // last either way, so they never crowd out the ranking.
          const na = leadingNumber(a.remarks);
          const nb = leadingNumber(b.remarks);
          if (na === null && nb === null) break;
          if (na === null) return 1;
          if (nb === null) return -1;
          if (na !== nb) return (na - nb) * dir;
          break;
        }
        case 'mobile':
          if ((a.agentMobile || '') !== (b.agentMobile || '')) {
            return (a.agentMobile || '').localeCompare(b.agentMobile || '') * dir;
          }
          break;
        case 'assigned': {
          // Unassigned rows are the actionable ones, so they lead on ascending.
          const va = a.assignedToName || a.assignedToCEANumber || '';
          const vb = b.assignedToName || b.assignedToCEANumber || '';
          if (va !== vb) return va.localeCompare(vb) * dir;
          break;
        }
        case 'progress': {
          const va = (a.calledAt ? 2 : 0) + (a.linkSentCount > 0 ? 1 : 0);
          const vb = (b.calledAt ? 2 : 0) + (b.linkSentCount > 0 ? 1 : 0);
          if (va !== vb) return (va - vb) * dir;
          break;
        }
        default:
          break;
      }
      const na = a.agentName || a.agentCEANumber;
      const nb = b.agentName || b.agentCEANumber;
      return na.localeCompare(nb) * (orderBy === 'agent' ? dir : 1);
    });
  }, [tasks, filters, sortConfig]);

  const setFilter = (patch: Partial<BoardFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const hasFilters = !filtersAreEmpty(filters);

  /** Options for the "Assigned to" filter: everyone the list is shared with, plus the pool. */
  const assignedFilterOptions = useMemo<FilterOption[]>(
    () => [
      { key: UNASSIGNED_KEY, label: 'Unassigned' },
      ...assigneeOptions.map((option) => ({ key: option.ceaNumber, label: option.name })),
    ],
    [assigneeOptions]
  );

  const progressFilterOptions: FilterOption[] = [
    { key: 'PENDING', label: 'Pending' },
    { key: 'CALLED', label: 'Called' },
  ];

  const sortDirectionFor = (column: SortKey): SortDirection =>
    sortConfig.column === column ? sortConfig.direction : 'default';

  const columnIsActive = (column: SortKey): boolean =>
    columnHasFilter(filters, column) || sortDirectionFor(column) !== 'default';

  const clearColumnFilter = (column: SortKey) => {
    switch (column) {
      case 'agent':
        setFilter({ agent: '' });
        break;
      case 'mobile':
        setFilter({ mobile: '' });
        break;
      case 'why':
        setFilter({ why: '' });
        break;
      case 'assigned':
        setFilter({ assigned: [] });
        break;
      case 'progress':
        setFilter({ progress: [] });
        break;
      default:
        break;
    }
  };

  const toggle = (taskId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // Select-all applies to what is on screen — with a filter active, "all" means
  // the rows the filter left, not the whole list.
  const toggleAll = () => {
    setSelected((prev) => {
      const visibleIds = visibleTasks.map((t) => t.id);
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      // Array.from, not spread: the build target predates Set iteration.
      return new Set([...Array.from(prev), ...visibleIds]);
    });
  };

  const handleAssign = async (unassign = false) => {
    if (selected.size === 0) return;
    if (!unassign && !assignee) return;
    setAssigning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.assignProjectActionTasks({
        taskIds: Array.from(selected),
        assignedToCEANumber: unassign ? null : assignee!.ceaNumber,
        // Only send the brief when one was typed, so assigning without a note
        // does not wipe briefs already on those rows.
        ...(brief.trim() ? { leadRemark: brief.trim() } : {}),
      });
      setSelected(new Set());
      setBrief('');
      if (openProjectId) await loadBoard(openProjectId);
      setNotice(
        unassign
          ? `Returned ${result.updated} agent${result.updated === 1 ? '' : 's'} to the pool.`
          : `Assigned ${result.updated} agent${result.updated === 1 ? '' : 's'} to ${
              assignee!.name || assignee!.ceaNumber
            }.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  const handleSaveBrief = async (taskId: number, leadRemark: string) => {
    try {
      await api.setProjectActionLeadRemark(taskId, leadRemark || null);
      setBoard((prev) =>
        prev
          ? { ...prev, tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, leadRemark: leadRemark || null } : t)) }
          : prev
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the note');
      if (openProjectId) await loadBoard(openProjectId);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const banner = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
    </>
  );

  // ---- project picker ----
  if (!openProjectId) {
    return (
      <>
        {banner}
        {summaries.length === 0 ? (
          <EmptyNote>No project you lead has agents to call yet.</EmptyNote>
        ) : (
          <TableContainer sx={paTableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={paHeadCellSx}>Project</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Agents</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Assigned</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Called</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Links sent</TableCell>
                  <TableCell sx={paHeadCellSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {summaries.map((row) => (
                  <TableRow key={row.projectId} hover>
                    <TableCell>{row.projectName || row.projectId}</TableCell>
                    <TableCell align="center">{row.total}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${row.assigned}/${row.total}`}
                        color={row.assigned === row.total ? 'success' : 'warning'}
                        variant={row.assigned === row.total ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">{row.called}</TableCell>
                    <TableCell align="center">{row.linksSent}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => setOpenProjectId(row.projectId)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    );
  }

  // ---- one project's board ----
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setOpenProjectId(null)}>
          All projects
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {board?.projectName || openProjectId}
        </Typography>
      </Stack>

      {listOptions.length > 1 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {listOptions.map((option) => (
            <Chip
              key={option.id}
              label={`${option.title} (${option.total})`}
              onClick={() => setSelectedListId(option.id)}
              color={option.id === selectedListId ? 'primary' : 'default'}
              variant={option.id === selectedListId ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      )}

      {activeList && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {activeList.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ color: 'rgb(var(--color-text-rgb) / 0.6)' }}>
              {hasFilters
                ? `Showing ${visibleTasks.length} of ${activeList.total} agents`
                : `${activeList.total} agents · ${activeList.unassigned} unassigned`}
            </Typography>
            {hasFilters && (
              <Button
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={() => setFilters(NO_FILTERS)}
                sx={{ textTransform: 'none' }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {banner}

      {listOptions.length === 0 ? (
        <EmptyNote>No agents on this project yet.</EmptyNote>
      ) : !activeList ? (
        <EmptyNote>
          {`This project has ${listOptions.length} lists — choose one above to work on.`}
        </EmptyNote>
      ) : (
        <>
          <TableContainer sx={paTableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={paHeadCellSx} padding="checkbox">
                    <Checkbox
                      checked={
                        visibleTasks.length > 0 && visibleTasks.every((t) => selected.has(t.id))
                      }
                      indeterminate={
                        visibleTasks.some((t) => selected.has(t.id)) &&
                        !visibleTasks.every((t) => selected.has(t.id))
                      }
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': 'Select all agents shown' }}
                    />
                  </TableCell>
                  <ColumnHeadCell
                    label="Agent"
                    minWidth={180}
                    active={columnIsActive('agent')}
                    onOpenMenu={(element) => setMenuAnchor({ element, column: 'agent' })}
                  />
                  <ColumnHeadCell
                    label="Mobile"
                    minWidth={110}
                    active={columnIsActive('mobile')}
                    onOpenMenu={(element) => setMenuAnchor({ element, column: 'mobile' })}
                  />
                  <ColumnHeadCell
                    label="Why"
                    minWidth={110}
                    active={columnIsActive('why')}
                    onOpenMenu={(element) => setMenuAnchor({ element, column: 'why' })}
                  />
                  <ColumnHeadCell
                    label="Assigned to"
                    minWidth={150}
                    active={columnIsActive('assigned')}
                    onOpenMenu={(element) => setMenuAnchor({ element, column: 'assigned' })}
                  />
                  <ColumnHeadCell label="My note to them" minWidth={200} />
                  <ColumnHeadCell label="Their comment" />
                  <ColumnHeadCell
                    label="Progress"
                    minWidth={120}
                    align="center"
                    active={columnIsActive('progress')}
                    onOpenMenu={(element) => setMenuAnchor({ element, column: 'progress' })}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTasks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{ color: 'rgb(var(--color-text-rgb) / 0.55)', fontStyle: 'italic' }}
                    >
                      No agents match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {visibleTasks.map((task) => (
                  <TableRow key={task.id} hover selected={selected.has(task.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(task.id)}
                        onChange={() => toggle(task.id)}
                        inputProps={{ 'aria-label': `Select ${task.agentCEANumber}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {task.agentName || task.agentCEANumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.55)' }}>
                        {task.agentCEANumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{task.agentMobile || '—'}</TableCell>
                    <TableCell>
                      {task.remarks ? <Chip size="small" label={task.remarks} variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell>
                      {task.assignedToCEANumber ? (
                        <>
                          <Typography variant="body2">
                            {task.assignedToName || task.assignedToCEANumber}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.5)' }}>
                            {formatDateTime(task.assignedAt)}
                          </Typography>
                        </>
                      ) : (
                        <Chip size="small" label="Unassigned" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <InlineCommentCell
                        value={task.leadRemark}
                        placeholder="Brief for the caller"
                        ariaLabel={`Note to the caller about ${task.agentName || task.agentCEANumber}`}
                        onSave={(next) => handleSaveBrief(task.id, next)}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: task.callRemark
                            ? 'var(--color-text-strong)'
                            : 'rgb(var(--color-text-rgb) / 0.45)',
                        }}
                      >
                        {task.callRemark || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Stack spacing={0.25} alignItems="center">
                        <Chip
                          size="small"
                          label={task.calledAt ? 'Called' : 'Pending'}
                          color={task.calledAt ? 'success' : 'default'}
                          variant={task.calledAt ? 'filled' : 'outlined'}
                        />
                        <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.5)' }}>
                          {`${task.linkSentCount} sent`}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <ColumnMenuPopover
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor?.element ?? null}
            label={menuAnchor ? COLUMN_LABELS[menuAnchor.column] : ''}
            direction={menuAnchor ? sortDirectionFor(menuAnchor.column) : 'default'}
            onDirectionChange={(direction) => {
              if (!menuAnchor) return;
              setSortConfig(
                direction === 'default'
                  ? { column: null, direction: 'default' }
                  : { column: menuAnchor.column, direction }
              );
            }}
            onClose={() => setMenuAnchor(null)}
            onClear={() => menuAnchor && clearColumnFilter(menuAnchor.column)}
            clearDisabled={!menuAnchor || !columnHasFilter(filters, menuAnchor.column)}
          >
            {menuAnchor &&
              (menuAnchor.column === 'assigned' || menuAnchor.column === 'progress' ? (
                <Autocomplete
                  multiple
                  size="small"
                  options={
                    menuAnchor.column === 'assigned' ? assignedFilterOptions : progressFilterOptions
                  }
                  value={(menuAnchor.column === 'assigned'
                    ? assignedFilterOptions
                    : progressFilterOptions
                  ).filter((option) =>
                    (menuAnchor.column === 'assigned' ? filters.assigned : filters.progress).includes(
                      option.key
                    )
                  )}
                  onChange={(_e, value) =>
                    setFilter(
                      menuAnchor.column === 'assigned'
                        ? { assigned: value.map((v) => v.key) }
                        : { progress: value.map((v) => v.key) }
                    )
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Any" label={COLUMN_LABELS[menuAnchor.column]} />
                  )}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder={menuAnchor.column === 'agent' ? 'Name or CEA' : 'Contains…'}
                  value={
                    menuAnchor.column === 'agent'
                      ? filters.agent
                      : menuAnchor.column === 'mobile'
                      ? filters.mobile
                      : filters.why
                  }
                  onChange={(e) =>
                    setFilter(
                      menuAnchor.column === 'agent'
                        ? { agent: e.target.value }
                        : menuAnchor.column === 'mobile'
                        ? { mobile: e.target.value }
                        : { why: e.target.value }
                    )
                  }
                />
              ))}
          </ColumnMenuPopover>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ mt: 1.5 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              options={board?.coreTeam || []}
              value={assignee}
              onChange={(_e, value) => setAssignee(value)}
              getOptionLabel={(option) => `${option.name || option.ceaNumber} · ${option.role || '—'}`}
              isOptionEqualToValue={(a, b) => a.ceaNumber === b.ceaNumber}
              renderInput={(params) => <TextField {...params} label="Assign to core team member" />}
            />
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              label="Note to the caller (optional)"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Applied to every agent you assign now"
            />
            <Button
              variant="contained"
              onClick={() => handleAssign(false)}
              disabled={selected.size === 0 || !assignee || assigning}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {assigning ? 'Assigning…' : `Assign ${selected.size}`}
            </Button>
            <Button
              color="inherit"
              onClick={() => handleAssign(true)}
              disabled={selected.size === 0 || assigning}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Unassign
            </Button>
          </Stack>
        </>
      )}
    </>
  );
};

export default ProjectBoardTab;
